import type { ReactNode } from 'react';
import { nanoid } from 'nanoid';

import type { EditableComponentProperties, PaddingProperty, PlacableComponent } from '../types';

export const DirectionTypes = ['row', 'row-reverse', 'column', 'column-reverse'] as const;
export type DirectionType = typeof DirectionTypes[number];

export const AlignItemsTypes = ['flex-start', 'flex-end', 'center', 'baseline', 'stretch'] as const;
export type AlignItemsType = typeof AlignItemsTypes[number];

export const JustifyContentTypes = [
  'flex-start',
  'flex-end',
  'center',
  'space-between',
  'space-around',
  'space-evenly'
] as const;
export type JustifyContentType = typeof JustifyContentTypes[number];

export interface LayoutComponentProperties<ContentType = ReactNode>
  extends PlacableComponent<'LAYOUT'> {
  alignItems: AlignItemsType;
  content: ContentType;
  direction: DirectionType;
  gap: string;
  height: string;
  justifyContent: JustifyContentType;
  padding?: PaddingProperty;
  width: string;
}

interface LayoutProps extends LayoutComponentProperties {
  className?: string;
}

export function Layout({
  alignItems,
  className,
  content,
  direction,
  gap,
  height,
  id,
  justifyContent,
  padding,
  width
}: LayoutProps) {
  return (
    <div
      className={className}
      id={id}
      style={{
        alignItems,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: direction,
        flexWrap: 'wrap',
        gap,
        height,
        justifyContent,
        paddingTop: padding?.top,
        paddingRight: padding?.right,
        paddingBottom: padding?.bottom,
        paddingLeft: padding?.left,
        position: 'relative',
        width
      }}>
      {content}
    </div>
  );
}

export function layoutComponentPropertiesFactory(): LayoutComponentProperties<
  Array<EditableComponentProperties>
> {
  return {
    alignItems: 'stretch',
    content: [],
    direction: 'row',
    gap: '1rem',
    height: '100%',
    id: nanoid(10),
    justifyContent: 'flex-start',
    padding: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    },
    type: 'LAYOUT',
    width: '100%'
  };
}
